'use client';

import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import { useTranslations } from 'next-intl';
import React from 'react';

import VideoAwareAutoHyperlink from '@/components/video/VideoAwareAutoHyperlink';

import { CinemaFaqDto } from '@cineamo/api/models/cinema-faq/CinemaFaqDto.types';

import { cn } from '@cineamo/ui/src/helpers/cn';

import { IconForthSolid16 } from '@cineamo/ui/src/icons/ic-forthSolid';

import { useAnalytics } from '@cineamo/composables/src/providers/analyticsProvider';

const FaqItem: React.FC<{ cinemaFaq: CinemaFaqDto }> = ({ cinemaFaq }) => {
    const { track } = useAnalytics();
    const t = useTranslations();
    return (
        <Disclosure
            as="div"
            className="w-full py-12 space-y-4">
            {({ open }) => (
                <>
                    <DisclosureButton
                        className="w-full flex items-start space-x-4 text-left text-body-bold-14"
                        aria-label={t('info.toggle-faq-aria-label', { question: cinemaFaq.question })}
                        onClick={() => {
                            if (!open) {
                                track('FAQ Question Opened', {
                                    questionId: cinemaFaq.id,
                                    questionText: cinemaFaq.question
                                });
                            }
                        }}>
                        <IconForthSolid16
                            className={cn('w-[16px] flex-none mt-2 duration-150', open ? 'rotate-90' : '')}
                        />
                        <span className="whitespace-pre-wrap">{cinemaFaq.question}</span>
                    </DisclosureButton>
                    <div className="overflow-hidden py-2">
                        <DisclosurePanel
                            transition
                            className={cn(
                                'origin-top duration-150 ease-in-out data-[closed]:opacity-0 data-[closed]:max-h-0',
                                'opacity-80 pl-[20px]',
                                'text-body-14 whitespace-pre-wrap',
                                open ? 'max-h-screen' : 'max-h-0'
                            )}>
                            <VideoAwareAutoHyperlink trackingEvent="FAQ Video Played">
                                {cinemaFaq.answer}
                            </VideoAwareAutoHyperlink>
                        </DisclosurePanel>
                    </div>
                </>
            )}
        </Disclosure>
    );
};

export default FaqItem;
